import { observer } from "mobx-react-lite";
import React, { FC, ReactElement, useRef, useState } from "react";
import { HeaderWithLogin } from "@/modules/login/ui";
import { langStore } from "@noli/business";
import styles from "./index.module.scss";
import ContentBlock from "@/pages/Setting/components/ContentBlock";
import ContentRow from "@/pages/Setting/components/ContentRow";
import { loginStore, UserInfo } from "@/modules/login/store";
import SetPassword from "@/pages/Setting/components/SetPassword";
import { getOSSConfigType, updateAvatar, updateUserInfo } from "@/pages/Setting/api";
import { Toast } from "@/components/Toast";
import { sensitiveWordCheck } from "@/utils/string";
import { CircularProgress } from "@chakra-ui/react";
import { SafeOp } from "@/utils/safeOp";
import Dialog from "@/pages/Setting/components/Dialog";
import BindPhone from "@/pages/Setting/components/BindPhone";
import BindWechat from "@/pages/Setting/components/BindWechat";
import { clientPut, createClient } from "@/utils/client";
import { generateUUID } from "@/utils/random";
import { SettingLangRecords } from "@/pages/Setting/lang";
import BindEmail from "@/pages/Setting/components/BindEmail";
import config from "@/config";
import { useOwnToast } from "@/utils/misc";

const Setting: FC<any> = (props) => {
  const lang = langStore.tgt;
  console.log(lang, 111);
  const t = useOwnToast();
  const avatarRef = useRef<HTMLInputElement | null>(null);
  const bannerRef = useRef<HTMLInputElement | null>(null);
  const {
    userState: { userInfo },
  } = loginStore;
  const [updateAvatarState, setUpdateAvatarState] = useState<number | null>(null);
  const [dialogComponent, setDialogComponent] = useState<{
    component: ReactElement<any, any>;
    title?: string;
    subtitle?: string;
  } | null>(null);
  const [bindWechatState, setBindWechatState] = useState<null | number>(null);
  const modifyAvatarOrBanner = async (file: File, type: number) => {
    setUpdateAvatarState(type);
    if (!["image/jpeg", "image/JPG", "image/png"].includes(file.type)) {
      Toast({
        title: "文件格式不正确，请重新上传，支持jpeg、jpg、png格式",
        status: "error",
      });
      avatarRef?.current?.value && (avatarRef.current.value = "");
      setUpdateAvatarState(null);
      return false;
    }
    if (file.size >= 12582912) {
      Toast({
        title: "图片大小不得大于12MB，请重新上传",
        status: "error",
      });
      avatarRef?.current?.value && (avatarRef.current.value = "");
      setUpdateAvatarState(null);
      return false;
    }
    const form = new FormData();
    form.append("file", file);
    async function failed(): Promise<void> {
      console.log("图有又问题");
    }
    try {
      const uploadRes = await SafeOp.uploadImage(t, lang, file, { failed, returnWH: false });
      if (!uploadRes) {
        return;
      }
      if (type === 2) {
        const { data } = await getOSSConfigType();
        const { client: _client } = await createClient({ ...data });
        const _fileSuff = file.type.slice(file.type.lastIndexOf("/") - file.type.length + 1);
        const { url } = await clientPut({
          file: file,
          client: _client,
          filePath: `wpsd/${process.env.REACT_ENV}/${generateUUID()}.${_fileSuff}`,
        });
        await updateUserInfo({
          ...userInfo,
          banner: url,
        });
      } else {
        await updateAvatar(form);
      }

      await loginStore.getUserInfo();
    } catch (e) {
      console.log(e);
    } finally {
      avatarRef?.current?.value && (avatarRef.current.value = "");
      bannerRef?.current?.value && (bannerRef.current.value = "");
      setUpdateAvatarState(null);
    }
  };
  const confirmModification = async (
    value: { [s: string]: unknown } | ArrayLike<unknown>,
    ref: { current: { value: string | number | boolean | string[] | number[] } },
    label: keyof UserInfo,
  ) => {
    try {
      Object.values(value).join("") && (await sensitiveWordCheck(Object.values(value).join("")));
      await updateUserInfo({
        ...userInfo,
        ...value,
      });
      await loginStore.getUserInfo();
      Toast({
        status: "success",
        title: "修改成功",
      });
    } catch (e) {
      Toast({
        status: "error",
        title: "昵称包含敏感词，请重新输入",
      });
      ref.current.value = userInfo[label] as string;
      console.error(e);
    }
  };
  const resetImg = async (type?: number) => {
    if (type === 1) {
      try {
        setUpdateAvatarState(2);
        await updateUserInfo({
          ...userInfo,
          banner: `${config.buildCdnBaseUrl}assets/creator/default-banner.jpg`,
        });
        await loginStore.getUserInfo();
      } catch (e) {
        console.error(e);
      } finally {
        setUpdateAvatarState(null);
      }
    }
  };
  return (
    <>
      <div className={styles.settingWrapper}>
        <HeaderWithLogin />
        <div className={styles.settingContentWrapper}>
          <div className={styles.headerRow}>
            {SettingLangRecords[lang]["Setting"]}
            <a href="/my_space">{SettingLangRecords[lang]["Back-to-Space"]}</a>
          </div>
          <ContentBlock title={SettingLangRecords[lang]["Account"]}>
            {lang !== "en" ? (
              <ContentRow
                rowEdit={true}
                label="绑定手机"
                suffix={userInfo?.isBindPhone ? "换绑手机" : "绑定手机"}
                value={
                  userInfo?.phone?.replace(userInfo?.phone.substring(3, 7), "****") ?? "未绑定"
                }
                border
                onSuffixClick={() => {
                  setDialogComponent({
                    component: (
                      <BindPhone
                        onClose={() => {
                          setDialogComponent(null);
                        }}
                      />
                    ),
                    title: "绑定新手机",
                  });
                }}
              />
            ) : (
              <ContentRow
                rowEdit={true}
                label="E-mail（Nolibox ID)*"
                suffix="Change E-mail"
                value={userInfo?.email}
                onSuffixClick={() => {
                  setDialogComponent({
                    component: (
                      <BindEmail
                        onClose={() => {
                          setDialogComponent(null);
                        }}
                      />
                    ),
                    title: "Change E-mail",
                  });
                }}
              />
            )}
            <ContentRow
              label={SettingLangRecords[lang]["Password*"]}
              suffix={SettingLangRecords[lang]["Change-Password"]}
              value="**********"
              border
              rowEdit={true}
              onSuffixClick={() => {
                setDialogComponent({
                  component: (
                    <SetPassword
                      onClose={() => {
                        setDialogComponent(null);
                      }}
                    />
                  ),
                  title: "设置密码",
                  subtitle: `使用手机 ${userInfo?.phone?.replace(
                    userInfo?.phone.substring(3, 7),
                    "****",
                  )} 验证`,
                });
              }}
            />
            {lang !== "en" && (
              <ContentRow
                rowEdit={true}
                label="微信"
                suffix={userInfo?.isBindWechat ? "解除绑定" : "绑定微信"}
                value={userInfo?.isBindWechat ? "已绑定" : "未绑定"}
                onSuffixClick={() => {
                  userInfo?.isBindWechat ? setBindWechatState(1) : setBindWechatState(2);
                }}
              />
            )}
          </ContentBlock>
          <ContentBlock title={SettingLangRecords[lang]["Space"]}>
            <ContentRow hideButton>
              <h6 className={styles.label} style={{ paddingLeft: 0 }}>
                {SettingLangRecords[lang]["Avatar"]}
              </h6>
              <div className={styles.avatarWrapper}>
                <div className={styles.avatarMain}>
                  <img src={userInfo?.avatar ?? ""} alt="" className={styles.avatar} />
                  {updateAvatarState === 1 && (
                    <div className={styles.CircularProgress}>
                      <CircularProgress isIndeterminate color="green.300" />
                    </div>
                  )}
                </div>
                <div className={styles.btnWrapper}>
                  <button
                    disabled={!!updateAvatarState}
                    onClick={() => {
                      avatarRef?.current?.click();
                    }}
                  >
                    {SettingLangRecords[lang]["Choose-Image"]}
                  </button>
                  {/*<button>初始化</button>*/}
                </div>
              </div>
              <h6 className={styles.label}>{SettingLangRecords[lang]["Background"]}</h6>
              <div
                className={styles.avatarWrapper}
                style={{ flex: 1, justifyContent: "space-between" }}
              >
                <div className={styles.avatarMain} style={{ flex: 1 }}>
                  <div
                    style={{
                      backgroundImage: `url(${
                        userInfo?.banner ||
                        `${config.buildCdnBaseUrl}assets/creator/default-banner.jpg`
                      })`,
                    }}
                    className={styles.banner}
                  />
                  {updateAvatarState === 2 && (
                    <div className={styles.CircularProgress}>
                      <CircularProgress isIndeterminate color="green.300" />
                    </div>
                  )}
                </div>
                <div className={styles.btnWrapper}>
                  <button
                    disabled={!!updateAvatarState}
                    onClick={() => {
                      bannerRef?.current?.click();
                    }}
                  >
                    {SettingLangRecords[lang]["Choose-Image"]}
                  </button>
                  <button
                    onClick={async () => {
                      await resetImg(1);
                    }}
                  >
                    {SettingLangRecords[lang]["Reset-Image"]}
                  </button>
                </div>
              </div>
            </ContentRow>
            <ContentRow
              disabled={!!updateAvatarState}
              label={SettingLangRecords[lang]["Display-Name*"]}
              suffix={SettingLangRecords[lang]["Edit-Name"]}
              value={userInfo?.nickname}
              suffixEnd={SettingLangRecords[lang]["Confirm"]}
              onSuffixClick={(value, ref) => {
                !updateAvatarState && confirmModification({ nickname: value }, ref, "nickname");
              }}
            />
            <ContentRow
              disabled={!!updateAvatarState}
              label={SettingLangRecords[lang]["Summary"]}
              suffix={SettingLangRecords[lang]["Edit-Summary"]}
              value={userInfo?.slogan ?? ""}
              suffixEnd={SettingLangRecords[lang]["Confirm"]}
              onSuffixClick={(value, ref) => {
                !updateAvatarState && confirmModification({ slogan: value }, ref, "slogan");
              }}
            />
          </ContentBlock>
        </div>
      </div>
      <input
        type="file"
        ref={avatarRef}
        className={styles.avatarRef}
        onChange={async (e) => {
          await modifyAvatarOrBanner(e.target.files?.[0] as File, 1);
        }}
      />
      <input
        type="file"
        ref={bannerRef}
        className={styles.avatarRef}
        onChange={async (e) => {
          await modifyAvatarOrBanner(e.target.files?.[0] as File, 2);
        }}
      />
      <Dialog
        visible={!!dialogComponent}
        onClose={() => {
          setDialogComponent(null);
        }}
        title={dialogComponent?.title as string}
        subtitle={dialogComponent?.subtitle as string}
      >
        {dialogComponent?.component}
      </Dialog>
      {bindWechatState && (
        <BindWechat
          type={bindWechatState}
          onClose={() => {
            setBindWechatState(null);
          }}
        />
      )}
    </>
  );
};
export default observer(Setting);
